import 'rxjs/add/operator/map';
import 'rxjs/add/observable/combineLatest';

import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import { ApiOptions, ApiProvider, EmptySubscriber } from '../api/api';
import {
  AppState,
  OPS_COMPLETED_ORDER_COLLECTION,
  OPS_ORDER_COLLECTION,
  getAll,
} from '../../state/state';
import {
  CancelledOrderParams,
  CreateAndCompleteOrderParams,
  OpsOrderModel,
  OrderCancelReason,
  OrderCompleteParams,
  OrderStatus,
} from '../../models/order.ops';
import { CollectionActions } from '../../state/actions';
import { FillerMessageTypes, MessageProvider } from '../message/message';
import { FillerShiftModel } from '../../models/filler-shift';
import { NoteCreateParams, NoteModel } from '../../models/note';
import {
  OpsVehicleModel,
  VehicleFillerUpdateParams,
} from '../../models/vehicle.ops';
import { SessionProvider } from '../session/session';
import { TruckModel } from '../../models/truck';
import { UserModel } from '../../models/user';
import { UserProvider } from '../user/user';

@Injectable()
export class OpsProvider {
  static SELECTED_TRUCK_KEY = 'selectedTruck';
  static SELECTED_ACCOUNT_KEY = 'selectedFleetAccount';

  selectedTruck$: BehaviorSubject<TruckModel> = new BehaviorSubject(null);
  selectedFleetAccount$: BehaviorSubject<UserModel> = new BehaviorSubject(null);
  currentFillerShift: FillerShiftModel;

  // Store Querys
  openOrders$: Observable<OpsOrderModel[]>;
  openOrderCount$: Observable<number>;
  completedOrders$: Observable<OpsOrderModel[]>;
  completedOrderCount$: Observable<number>;
  completedOrdersThisShift$: Observable<OpsOrderModel[]>;
  confirmedOrders$: Observable<OpsOrderModel[]>;
  loadingOpenOrders$: Observable<boolean>;
  loadingCompletedOrders$: Observable<boolean>;
  completedOnTimePercentage$: Observable<number>;

  constructor(
    private store: Store<AppState>,
    private collectionActions: CollectionActions,
    private userProvider: UserProvider,
    private session: SessionProvider,
    private api: ApiProvider,
    private msgService: MessageProvider
  ) {
    this.getCurrentFillerShift();
    this.openOrders$ = store.select(state =>
      getAll<OpsOrderModel>(state.opsOrders)
    );
    this.openOrderCount$ = store.select(state => state.opsOrders.total_result);
    this.completedOrders$ = store.select(state =>
      getAll<OpsOrderModel>(state.opsCompletedOrders)
    );
    this.completedOrderCount$ = store.select(
      state => state.opsCompletedOrders.total_result
    );
    this.confirmedOrders$ = store.select(state =>
      this.confirmedOrders(getAll<OpsOrderModel>(state.opsOrders))
    );
    this.loadingOpenOrders$ = store.select(state => state.opsOrders.loading);
    this.loadingCompletedOrders$ = store.select(
      state => state.opsCompletedOrders.loading
    );
    this.completedOnTimePercentage$ = store.select(state =>
      this.computeOnTimePercentage(
        getAll<OpsOrderModel>(state.opsCompletedOrders)
      )
    );
    this.completedOrdersThisShift$ = store.select(state =>
      this.ordersCompletedThisShift(
        getAll<OpsOrderModel>(state.opsCompletedOrders)
      )
    );
  }

  // Shift
  private getCurrentFillerShift() {
    this.userProvider.getCurrentFillerShift();
    this.userProvider.currentFillerShift$.subscribe(
      (shift: FillerShiftModel) => {
        this.currentFillerShift = shift;
        this.currentFillerShift &&
          this.selectedTruck$.next(this.currentFillerShift.truck);
      }
    );
  }

  public startShift(truck: TruckModel): Observable<FillerShiftModel> {
    if (truck) {
      let request = this.api
        .call({
          url: `/trucks/${truck.uid}/filler_shifts`,
          method: 'POST',
          ops: true,
        })
        .map(result => new FillerShiftModel(result));
      request
        .do(() => {
          this.userProvider.getUser();
        })
        .subscribe(new EmptySubscriber());
      return request;
    }
    return Observable.throw('no truckId present');
  }

  public endShift(shift: FillerShiftModel): Observable<FillerShiftModel> {
    let request = new Observable<FillerShiftModel>();
    if (shift && shift.uid && shift.truck && shift.truck.uid) {
      request = this.api.call({
        url: `/trucks/${shift.truck.uid}/filler_shifts/${shift.uid}`,
        method: 'PATCH',
        ops: true,
      });
      request
        .do(() => {
          this.setSelectedTruck(null);
          this.userProvider.getUser();
        })
        .subscribe(new EmptySubscriber());
    }
    return request;
  }

  // Orders

  getOrdersForTruck(
    truck: TruckModel,
    pageNumber?,
    preventUpdatingStore?: boolean
  ) {
    this.store.dispatch(
      this.collectionActions.setLoading(OPS_ORDER_COLLECTION, true)
    );
    let options: ApiOptions = {
      url: `/fillers/trucks/${truck.uid}/schedule`,
      method: 'GET',
      includePageInfo: true,
      ops: true,
    };
    if (pageNumber || pageNumber === 0) {
      options.params = { page: pageNumber };
    }
    if (pageNumber === 1 && preventUpdatingStore) {
      options.compareEtag = true;
    }
    let request = this.api
      .call(options)
      .map(result => {
        return {
          items: result.response.map(item => new OpsOrderModel(item)),
          pageInfo: result.pageInfo,
          status: result.status,
        };
      })
      .finally(() =>
        this.store.dispatch(
          this.collectionActions.setLoading(OPS_ORDER_COLLECTION, false)
        )
      );
    request
      .do(pagedItems => {
        if (!preventUpdatingStore) {
          pagedItems.items = this.sortClaimedOrders(pagedItems.items);
          if (pageNumber > 1) {
            this.store.dispatch(
              this.collectionActions.append(
                OPS_ORDER_COLLECTION,
                pagedItems.items
              )
            );
          } else {
            this.store.dispatch(
              this.collectionActions.setWithPageInfo(
                OPS_ORDER_COLLECTION,
                pagedItems
              )
            );
          }
        }
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  createMeetingURL(uid: string) {
    return this.api
      .call({
        url: `/fillers/orders/${uid}/meetings`,
        method: 'POST',
        ops: true,
      })
      .flatMap(() => {
        return this.getOrder(uid);
      });
  }

  getOrder(uid: string) {
    let request = this.api
      .call({
        url: `/fillers/orders/${uid}`,
        method: 'GET',
        ops: true,
      })
      .map(item => new OpsOrderModel(item));
    request
      .do(order => {
        this.store.dispatch(
          this.collectionActions.update(OPS_ORDER_COLLECTION, order)
        );
        this.store.dispatch(
          this.collectionActions.update(OPS_COMPLETED_ORDER_COLLECTION, order)
        );
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  // Subscribes to updates to any particular order in the application store

  subscribeToOrder(uid: string) {
    let updatedOpenOrders$ = this.store.select(state =>
      getAll<OpsOrderModel>(state.opsOrders)
    );
    let updatedCompletedOrders$ = this.store.select(state =>
      getAll<OpsOrderModel>(state.opsCompletedOrders)
    );
    return updatedOpenOrders$
      .merge(updatedCompletedOrders$)
      .map((orders: OpsOrderModel[]) => {
        return orders.filter((order: OpsOrderModel) => {
          return order.uid == uid;
        })[0];
      })
      .distinct();
  }

  createCancelledOrder(params: CancelledOrderParams) {
    return this.api
      .call({
        url: `/fillers/orders/`,
        method: 'POST',
        ops: true,
        params: params,
      })
      .map(data => new OpsOrderModel(data));
  }

  createAndCompleteOrder(params: CreateAndCompleteOrderParams) {
    let request = this.api
      .call({
        url: `/fillers/orders/create_and_complete`,
        method: 'POST',
        ops: true,
        params: params,
      })
      .map(data => new OpsOrderModel(data));
    request
      .do(order => {
        this.store.dispatch(
          this.collectionActions.insert(OPS_COMPLETED_ORDER_COLLECTION, order)
        );
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  getCompletedOrders(pageNumber?) {
    this.store.dispatch(
      this.collectionActions.setLoading(OPS_COMPLETED_ORDER_COLLECTION, true)
    );
    let options: ApiOptions = {
      url: `/fillers/orders/completed`,
      method: 'GET',
      includePageInfo: true,
      ops: true,
    };
    if (pageNumber) {
      options.params = { page: pageNumber };
    }
    let request = this.api
      .call(options)
      .map(result => {
        return {
          items: result.response.map(item => new OpsOrderModel(item)),
          pageInfo: result.pageInfo,
        };
      })
      .finally(() =>
        this.store.dispatch(
          this.collectionActions.setLoading(
            OPS_COMPLETED_ORDER_COLLECTION,
            false
          )
        )
      );
    request
      .do(pagedItems => {
        if (pageNumber > 1) {
          this.store.dispatch(
            this.collectionActions.append(
              OPS_COMPLETED_ORDER_COLLECTION,
              pagedItems.items
            )
          );
        } else {
          this.store.dispatch(
            this.collectionActions.setWithPageInfo(
              OPS_COMPLETED_ORDER_COLLECTION,
              pagedItems
            )
          );
        }
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  completeOrder(order: OpsOrderModel, params?: OrderCompleteParams) {
    let request = this.api
      .call({
        url: `/fillers/orders/${order.uid}/complete`,
        method: 'POST',
        ops: true,
        params: params,
      })
      .flatMap(() => {
        return this.getOrder(order.uid);
      });
    request
      .do(order => {
        this.store.dispatch(
          this.collectionActions.insert(OPS_COMPLETED_ORDER_COLLECTION, order)
        );
        this.store.dispatch(
          this.collectionActions.delete(OPS_ORDER_COLLECTION, order)
        );
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  cancelOrder(order: OpsOrderModel, reason: OrderCancelReason) {
    let request = this.api
      .call({
        url: `/fillers/orders/${order.uid}/cancel`,
        method: 'POST',
        ops: true,
        params: { cancellation_reason: reason },
      })
      .flatMap(() => {
        return this.getOrder(order.uid);
      });
    request
      .do(order => {
        this.store.dispatch(
          this.collectionActions.delete(OPS_COMPLETED_ORDER_COLLECTION, order)
        );
        this.store.dispatch(
          this.collectionActions.delete(OPS_ORDER_COLLECTION, order)
        );
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  deleteOrder(order: OpsOrderModel) {
    let request = this.api
      .call({
        url: `/fillers/orders/${order.uid}`,
        method: 'DELETE',
        ops: true,
      })
      .map(data => new OpsOrderModel(data));
    request
      .do(order => {
        this.store.dispatch(
          this.collectionActions.delete(OPS_COMPLETED_ORDER_COLLECTION, order)
        );
        this.store.dispatch(
          this.collectionActions.delete(OPS_ORDER_COLLECTION, order)
        );
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  startDrivingToOrder(order: OpsOrderModel) {
      let request = this.api
        .call({
          url: `/fillers/orders/${order.uid}/start_driving`,
          method: 'POST',
          ops: true,
        })
        .flatMap(() => this.getOrder(order.uid));
      return request;
  }

  startDrivingToOrders(orders: OpsOrderModel[]) {
    let requests = [];
    for (let order of orders) {
      let request = this.api
        .call({
          url: `/fillers/orders/${order.uid}/start_driving`,
          method: 'POST',
          ops: true,
        })
        .flatMap(() => this.getOrder(order.uid));
      requests.push(request);
    }
    return Observable.combineLatest(requests);
  }

  sendETAForOrder(order: OpsOrderModel) {
    return Observable.fromPromise(this.getCurrentPosition())
      .flatMap(position => this.getEstimatedTimeToOrder(position, order))
      .flatMap(time => this.saveEtaForOrder(order, time))
      .flatMap(() => this.getOrder(order.uid));
  }

  sendETAToOrders(orders: OpsOrderModel[]) {
    return Observable.fromPromise(this.getCurrentPosition())
      .flatMap(position => this.getEstimatedTimeToOrder(position, orders[0]))
      .flatMap(time => this.saveEtaForOrders(orders, time));
  }

  clearETAForOrder(order: OpsOrderModel) {
    let request = this.api
      .call({
        url: `/fillers/orders/${order.uid}/order_eta`,
        method: 'DELETE',
        ops: true,
      })
      .flatMap(() => {
        return this.getOrder(order.uid);
      });
    return request;
  }

  private getEstimatedTimeToOrder(currentPosition, order) {
    let params: google.maps.DirectionsRequest = {
      origin: new google.maps.LatLng(
        currentPosition.latitude,
        currentPosition.longitude
      ),
      destination: new google.maps.LatLng(order.lat, order.lng),
      travelMode: google.maps.TravelMode.DRIVING,
      provideRouteAlternatives: false,
      avoidHighways: false,
    };
    return Observable.create(observer => {
      let directionsService = new google.maps.DirectionsService();
      directionsService.route(params, (result, status) => {
        if (status == google.maps.DirectionsStatus.OK) {
          let ms = result.routes[0].legs[0].duration.value * 1000;
          let time = new Date(new Date().getTime() + ms);
          observer.next(time);
          observer.complete();
        } else {
          observer.error(new Error('Error getting eta'));
        }
      });
    });
  }

  private saveEtaForOrder(order, time) {
    return this.api.call({
      url: '/order_etas',
      method: 'POST',
      ops: true,
      convertToParams: true,
      body: {
        eta: moment(time).format('HH:mm'),
        order_uids: [order.uid],
      },
    });
  }

  private saveEtaForOrders(orders, time) {
    return this.api.call({
      url: '/order_etas',
      method: 'POST',
      ops: true,
      convertToParams: true,
      body: {
        eta: moment(time).format('HH:mm'),
        order_uids: orders.map(order => order.uid),
      },
    });
  }

  private getCurrentPosition(): Promise<CurrentPosition> {
    if (
      typeof navigator === 'undefined' ||
      !navigator.geolocation ||
      !navigator.geolocation.getCurrentPosition
    ) {
      return Promise.reject('Geolocation is not supported by this browser.');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        position =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            heading: position.coords.heading,
          }),
        () => reject('Unable to determine your current location.'),
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 10000,
        }
      );
    });
  }

  // Notes

  getCustomerNotes(uid: string): Observable<NoteModel[]> {
    let request = this.api
      .call({
        url: `/users/${uid}/notes`,
        params: {
          notated_object: 'User',
          uid: uid,
        },
        method: 'GET',
      })
      .map(res => res.map(note => new NoteModel(note)));
    request.subscribe(new EmptySubscriber());
    return request;
  }

  addNoteForCustomer(uid, params: NoteCreateParams) {
    return this.api
      .call({
        url: `/users/${uid}/notes?uid=${uid}`,
        method: 'POST',
        params: params,
      })
      .map(data => new NoteModel(data));
  }

  // Messages

  private _sentMessages = {};
  sendMessageForOrder(
    order: OpsOrderModel,
    fillerMessageType: FillerMessageTypes
  ) {
    return this.msgService.sendMessageToCustomer(
      order,
      fillerMessageType,
      this._sentMessages
    );
  }

  didSendMessageForOrder(
    order: OpsOrderModel,
    fillerMessageType: FillerMessageTypes
  ) {
    let key = fillerMessageType + order.uid;
    return this._sentMessages[key] == true;
  }


  // Vehicles

  vehicleLookup(
    user: UserModel,
    type: 'vin' | 'license' | 'serial_number',
    query
  ): Observable<OpsVehicleModel[]> {
    let params: any = {};
    params[type] = query;
    params.account_uid = user.uid;
    return this.api
      .call({
        url: `/fillers/vehicles/lookup`,
        method: 'GET',
        ops: true,
        params: params,
      })
      .map(result => result.map(item => new OpsVehicleModel(item)));
  }

  updateVehicleForOrder(
    order: OpsOrderModel,
    params: VehicleFillerUpdateParams
  ) {
    let request = this.api
      .call({
        url: `/fillers/vehicles/${order.vehicle.uid}`,
        method: 'PATCH',
        ops: true,
        params: params,
      })
      .flatMap(() => {
        return this.getOrder(order.uid);
      });
    return request;
  }

  saveMiles(vehicle: OpsVehicleModel, mileage: number) {
    return this.api.call({
      url: `/vehicles/${vehicle.uid}/mileages`,
      method: 'POST',
      params: {
        mileage,
      },
      ops: true,
    });
  }

  // Trucks

  getTrucks(pageNumber: number = 1, paginate: boolean = true) {
    const options = {
      url: `/fillers/trucks`,
      method: 'GET',
      params: {},
      ops: true,
    };
    if (paginate) {
      options.params['page'] = pageNumber;
    }
    let request = this.api.call(options).map(result => {
      return result.map(item => new TruckModel(item));
    });
    return request;
  }

  getTruck(uid: string) {
    return this.api
      .call({
        url: `/fillers/trucks/${uid}`,
        method: 'GET',
        ops: true,
      })
      .map(result => new TruckModel(result));
  }

  setSelectedTruck(truck: TruckModel) {
    let payload = truck ? truck.json : null;
    this.session.set(OpsProvider.SELECTED_TRUCK_KEY, payload);
    this.selectedTruck$.next(truck);
    this.getOrdersForTruck(truck, 1);
  }

  loadSelectedTruck() {
    return this.session.get(OpsProvider.SELECTED_TRUCK_KEY).then(data => {
      if (this.currentFillerShift) {
        this.selectedTruck$.next(this.currentFillerShift.truck);
      } else if (data) {
        let truck = new TruckModel(data);
        this.selectedTruck$.next(truck);
        this.getTruck(truck.uid).subscribe(truck => {
          this.session.set(OpsProvider.SELECTED_TRUCK_KEY, truck);
          this.selectedTruck$.next(truck);
        });
      } else {
        this.selectedTruck$.next(null);
      }
    });
  }

  // Fleets

  loadSelectedFleetAccount() {
    this.session.get(OpsProvider.SELECTED_ACCOUNT_KEY).then(data => {
      if (data) {
        let account = new UserModel(data);
        this.selectedFleetAccount$.next(account);
      } else {
        this.selectedFleetAccount$.next(null);
      }
    });
  }

  setSelectedFleetAccount(account: UserModel) {
    let payload = account ? account.json : null;
    this.session.set(OpsProvider.SELECTED_ACCOUNT_KEY, payload);
    this.selectedFleetAccount$.next(account);
  }

  getFleetAccounts() {
    return this.api
      .call({
        url: `/fillers/accounts/fleet`,
        method: 'GET',
        ops: true,
      })
      .map(result => result.map(item => new UserModel(item)));
  }

  // Helpers

  private confirmedOrders(orders: OpsOrderModel[]) {
    return orders.filter(order => order.deliveryLocation);
  }

  private sortClaimedOrders(orders: OpsOrderModel[]): OpsOrderModel[] {
    const claimedOrders: OpsOrderModel[] = [];
    const otherOrders: OpsOrderModel[] = [];
    orders.forEach(order =>
      order.status === OrderStatus.Claimed
        ? claimedOrders.push(order)
        : otherOrders.push(order)
    );
    return claimedOrders.concat(otherOrders);
  }

  private computeOnTimePercentage(completedOrders: OpsOrderModel[]): number {
    if (!completedOrders.length || !this.currentFillerShift) {
      return null;
    }
    const completedThisShift = this.ordersCompletedThisShift(completedOrders);
    const completedOnTime = completedThisShift.filter(
      order => order.completedOnTime
    );
    return completedThisShift
      ? (completedOnTime.length / completedThisShift.length) * 100
      : 0;
  }

  private ordersCompletedThisShift(orders: OpsOrderModel[]): OpsOrderModel[] {
    if (!orders || !this.currentFillerShift) {
      return [];
    }
    return orders.filter(order => {
      return (
        order.timeCompleted &&
        moment(order.timeCompleted).isAfter(this.currentFillerShift.clockIn)
      );
    });
  }
}

interface CurrentPosition {
  latitude: number;
  longitude: number;
  heading?: number;
}



// WEBPACK FOOTER //
// ./src/providers/ops/ops.ts